/**
 * Easter Egg Registry — Djay Mascot Domain
 *
 * DDD co-locatie: Easter eggs zijn onderdeel van het mascotte-domein.
 * Ze gebruiken bestaande DjayEmotion values en worden getriggerd door
 * gebruikersinteracties met Djay of de app.
 *
 * Elk Easter egg heeft:
 * - Een uniek ID voor persistentie
 * - Een trigger-conditie (pure function)
 * - Een Djay-reactie (emotion + message)
 * - Optioneel: een reward (variant unlock, sound, etc.)
 *
 * Usage:
 *   import { EASTER_EGGS, checkTimeBasedEgg } from './easterEggs'
 */

import type { DjayEmotion } from './DjayAvatar';
import { RAPID_TAP_CONFIG, TIME_EGG_HOURS } from './config';

// ── Types ────────────────────────────────────────────────────

export type EasterEggId = 'rapid-tap' | 'konami-code' | 'nacht-uil' | 'vroege-vogel' | 'long-press-goud';
export interface EasterEggReaction {
  text: string;
  emotion: DjayEmotion;
}
export interface EasterEggDefinition {
  id: EasterEggId;
  /** Display name (Dutch) */
  name: string;
  /** Short description for discovery log */
  description: string;
  /** The Djay reaction when triggered */
  reaction: EasterEggReaction;
  /** Can this egg trigger more than once? */
  repeatable: boolean;
  /** Optional: sound to play (key from soundManager) */
  sound?: EasterEggSound;
}

/** Type-safe sound keys that exist on soundManager */
export type EasterEggSound = 'playAchievement' | 'playDjaySpeak' | 'playLevelUp';

// ── Registry ─────────────────────────────────────────────────

export const EASTER_EGGS: Record<EasterEggId, EasterEggDefinition> = {
  'rapid-tap': {
    id: 'rapid-tap',
    name: 'Kietelaar',
    description: 'Tik 5x snel op Djay',
    reaction: {
      text: 'Hahaha, dat kietelt! 🤣 Stop, stop!',
      emotion: 'surprised'
    },
    repeatable: true,
    sound: 'playDjaySpeak'
  },
  'konami-code': {
    id: 'konami-code',
    name: 'Geheime Code',
    description: 'Voer de Konami Code in',
    reaction: {
      text: 'Geheim gevonden! Je bent een echte gamer! 🎮✨',
      emotion: 'celebrating'
    },
    repeatable: false,
    sound: 'playAchievement'
  },
  'nacht-uil': {
    id: 'nacht-uil',
    name: 'Nacht-Uil',
    description: 'Oefen na 22:00',
    reaction: {
      text: 'Jij bent een echte nacht-uil! 🦉 Vergeet niet om te slapen, hè!',
      emotion: 'sleeping'
    },
    repeatable: true,
    sound: 'playDjaySpeak'
  },
  'vroege-vogel': {
    id: 'vroege-vogel',
    name: 'Vroege Vogel',
    description: 'Oefen voor 07:00',
    reaction: {
      text: 'Wauw, zo vroeg al?! 🐦 De vroege vogel vangt de worm!',
      emotion: 'surprised'
    },
    repeatable: true,
    sound: 'playDjaySpeak'
  },
  'long-press-goud': {
    id: 'long-press-goud',
    name: 'DJ Goud',
    description: 'Houd Djay lang ingedrukt',
    reaction: {
      text: 'Geheim ontdekt! DJ Goud ontgrendeld! ✨',
      emotion: 'celebrating'
    },
    repeatable: false,
    sound: 'playAchievement'
  }
};

/**
 * Escalating rapid-tap reactions — Djay gets increasingly flustered
 * the more times you trigger the rapid-tap in one session.
 */
export const RAPID_TAP_ESCALATION: EasterEggReaction[] = [{
  text: 'Hahaha, dat kietelt! 🤣 Stop, stop!',
  emotion: 'surprised'
}, {
  text: 'Hé! Niet weer! 😂 Ik ben geen knopje!',
  emotion: 'surprised'
}, {
  text: 'AAAH! Oké oké, je wint! 🏳️😵',
  emotion: 'celebrating'
}, {
  text: 'Je geeft niet op hè? Ik ook niet! 💪🤣',
  emotion: 'excited'
}, {
  text: '...ben je klaar? 😅 Ik word er duizelig van!',
  emotion: 'thinking'
}, {
  text: 'Oké, ik tel mee: dat was nummer ${count}! 🔢😂',
  emotion: 'pointing'
}, {
  text: 'Je bent officieel de Kietelkampioen! 🏆🤣',
  emotion: 'celebrating'
}];

// ── Trigger Helpers ──────────────────────────────────────────

/** Check if a time-based Easter egg should trigger */
export function checkTimeBasedEgg(): EasterEggId | null {
  const hour = new Date().getHours();
  if (hour >= TIME_EGG_HOURS.NIGHT_OWL_START || hour < TIME_EGG_HOURS.NIGHT_OWL_END) return 'nacht-uil';
  if (hour >= TIME_EGG_HOURS.NIGHT_OWL_END && hour < TIME_EGG_HOURS.EARLY_BIRD_END) return 'vroege-vogel';
  return null;
}

/**
 * Rapid-tap detector with cooldown and escalation support.
 * Defaults pulled from RAPID_TAP_CONFIG (mascot/config.ts).
 */
export function createRapidTapDetector(threshold = RAPID_TAP_CONFIG.THRESHOLD, windowMs = RAPID_TAP_CONFIG.WINDOW_MS, cooldownMs = RAPID_TAP_CONFIG.COOLDOWN_MS): {
  recordTap: () => {
    triggered: boolean;
    inCooldown: boolean;
    triggerCount: number;
  };
  reset: () => void;
  getTriggerCount: () => number;
} {
  let taps: number[] = [];
  let cooldownUntil = 0;
  let triggerCount = 0;
  let hasTriggeredThisBurst = false;
  return {
    recordTap() {
      const now = Date.now();

      // If in cooldown, absorb the tap
      if (now < cooldownUntil) {
        return {
          triggered: false,
          inCooldown: true,
          triggerCount
        };
      }
      taps.push(now);
      // Remove taps outside the window
      taps = taps.filter(t => now - t < windowMs);
      if (taps.length >= threshold && !hasTriggeredThisBurst) {
        hasTriggeredThisBurst = true;
        triggerCount++;
        cooldownUntil = now + cooldownMs;
        // Don't reset taps — let the window expire naturally
        return {
          triggered: true,
          inCooldown: false,
          triggerCount
        };
      }

      // If taps dropped below threshold, allow re-triggering
      if (taps.length < threshold) {
        hasTriggeredThisBurst = false;
      }
      return {
        triggered: false,
        inCooldown: false,
        triggerCount
      };
    },
    reset() {
      taps = [];
      cooldownUntil = 0;
      hasTriggeredThisBurst = false;
      // Note: triggerCount persists across resets (session-level counter)
    },
    getTriggerCount() {
      return triggerCount;
    }
  };
}

/** Konami code sequence detector */
export function createKonamiDetector(): {
  handleKey: (key: string) => boolean;
  reset: () => void;
} {
  const SEQUENCE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
  let position = 0;
  return {
    handleKey(key: string): boolean {
      if (key === SEQUENCE[position]) {
        position++;
        if (position === SEQUENCE.length) {
          position = 0;
          return true;
        }
      } else {
        position = key === SEQUENCE[0] ? 1 : 0;
      }
      return false;
    },
    reset() {
      position = 0;
    }
  };
}

/**
 * Get the escalated rapid-tap reaction based on how many times
 * the user has triggered it this session.
 */
export function getRapidTapReaction(triggerCount: number): EasterEggReaction {
  const index = Math.min(triggerCount - 1, RAPID_TAP_ESCALATION.length - 1);
  const reaction = RAPID_TAP_ESCALATION[index];
  // Replace ${count} placeholder if present
  return {
    ...reaction,
    text: reaction.text.replace('${count}', String(triggerCount))
  };
}